import FilterAltIcon from '@mui/icons-material/FilterAlt'
import {
  Box,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  TableSortLabel,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useDispatch } from 'react-redux'

import { clearSelectedCases, clearTrashSelectedCases } from '@/store/project'
import { statusLabelMap } from '@/utils/constants'

import type { ProjectStatus } from '.'

interface StyledSortLabelProps {
  label: string
  filters?: ProjectStatus[]
  setFilters?: (filters: ProjectStatus[]) => void
  ordering?: string
  setOrdering?: (field: string) => void
}

const columnKeyMap: Record<string, string> = {
  Name: 'name',
  Status: 'status',
  Dentist: 'dentist',
  Access: 'access',
  'Shared With': 'shared_with',
  'Created At': 'created_ts',
  'Created By': 'created_by',
  'Last Modified At': 'updated_ts',
}

const StyledSortLabel = ({
  label,
  filters = [],
  setFilters,
  ordering,
  setOrdering,
}: StyledSortLabelProps) => {
  const dispatch = useDispatch()
  const isStatus = label === 'Status'
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const field = columnKeyMap[label] ?? ''
  const isActive = ordering?.includes(field)
  const isAsc = isActive && !ordering?.startsWith('-')

  const handleSortClick = () => {
    if (!field || !setOrdering) return
    setOrdering(isAsc ? `-${field}` : field)
    dispatch(clearSelectedCases())
    dispatch(clearTrashSelectedCases())
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleToggle = (status: ProjectStatus) => {
    if (!setFilters) return
    const newFilters = filters.includes(status)
      ? filters.filter((s) => s !== status)
      : [...filters, status]
    setFilters(newFilters)
    setAnchorEl(null)
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <TableSortLabel
        active
        direction={isAsc ? 'asc' : 'desc'}
        onClick={handleSortClick}
        sx={{
          color: 'white',
          '& .MuiTableSortLabel-icon': {
            color: 'white !important',
          },
        }}
      >
        {label}
      </TableSortLabel>

      {isStatus && (
        <>
          <IconButton onClick={handleClick} size="small">
            <FilterAltIcon sx={{ fontSize: 18, color: 'white' }} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                backgroundColor: '#1e1e1e',
                color: 'white',
                width: '160px',
              },
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {(['active', 'scheduled', 'archive'] as ProjectStatus[]).map(
              (status) => (
                <MenuItem
                  key={status}
                  onClick={() => handleToggle(status)}
                  disableRipple
                  sx={{
                    p: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '9px',
                    minHeight: 'auto',
                  }}
                >
                  <Checkbox
                    checked={filters.includes(status)}
                    sx={{
                      p: 0.5,
                      color: '#90caf9',
                      '&.Mui-checked': { color: '#90caf9' },
                      height: 20,
                      width: 20,
                    }}
                  />
                  <Typography
                    sx={{
                      lineHeight: '24px',
                      fontSize: '16px',
                      fontWeight: 400,
                    }}
                  >
                    {statusLabelMap[status]}
                  </Typography>
                </MenuItem>
              ),
            )}
          </Menu>
        </>
      )}
    </Box>
  )
}

export default StyledSortLabel
